/**
 * Blank legal-template download client — the Welcome-page "Blank legal templates"
 * card (#3, Pedro 2026-09-13). Firm-level and agreement-independent: the same
 * templates as the per-agreement annex exports, but the backend renders the
 * variable blocks blank/generic (IAA empty placeholders; Annex I with the Standard
 * fee schedule; AQ/P&P/Sample MR served as-is). Same raw same-origin fetch +
 * Content-Disposition filename pattern as the annex exports; reuses downloadBlob.
 */
import { feeSchedulesApi, type FeeFrequency } from './feeSchedules';
import { downloadBlob } from './iaaExports';
import type { DocumentLanguage } from './types';

export { downloadBlob };

export type LegalTemplateKind =
  | 'iaa' | 'fees' | 'aq' | 'pp' | 'samplemonthly'
  | 'clientform_individual' | 'clientform_legal';   // P16a: blank client-record forms (PDF)
export type LegalTemplateFormat = 'word' | 'pdf';

async function detailOf(res: Response, fallback: string): Promise<string> {
  let detail: unknown = null;
  try { detail = (await res.json()).detail; } catch { /* non-JSON */ }
  return typeof detail === 'string' ? detail : fallback;
}

export async function downloadLegalTemplate(
  kind: LegalTemplateKind,
  language: DocumentLanguage,
  format: LegalTemplateFormat = 'word',
): Promise<{ blob: Blob; fileName: string }> {
  const res = await fetch(
    `/legal-templates/${kind}?language=${language}&format=${format}`,
    { method: 'GET', credentials: 'include' },
  );
  if (!res.ok) {
    throw new Error(await detailOf(res, 'The template could not be generated.'));
  }
  const cd = res.headers.get('Content-Disposition') ?? '';
  const m = /filename\*=UTF-8''([^;]+)/.exec(cd);
  const fileName = m ? decodeURIComponent(m[1]) : `${kind}.${format === 'pdf' ? 'pdf' : 'docx'}`;
  return { blob: await res.blob(), fileName };
}

/** The blank Annex I PDF for a chosen schedule with the fee calculator's worked example
 *  under its terms (P22; Pedro 2026-10-10: reached from the templates Download, not a
 *  separate button). */
export async function downloadFeeExamplePdf(
  scheduleId: string,
  amountEur: number,
  language: DocumentLanguage,
  frequency: FeeFrequency,
): Promise<{ blob: Blob; fileName: string }> {
  const res = await fetch(
    feeSchedulesApi.examplePdfHref(scheduleId, amountEur, language, frequency),
    { method: 'GET', credentials: 'include' },
  );
  if (!res.ok) {
    throw new Error(await detailOf(res, 'The Annex I with the fee simulation could not be generated.'));
  }
  const cd = res.headers.get('Content-Disposition') ?? '';
  const m = /filename="([^"]+)"/.exec(cd);
  const fileName = m ? m[1] : `Baluarte_Annex_I_Fees_example_${language}.pdf`;
  return { blob: await res.blob(), fileName };
}
